import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  BooksIcon,
  BuildingsIcon,
  UsersThreeIcon,
  ArrowBendUpRightIcon,
  InfoIcon,
  LinkSimpleIcon,
  PlusIcon,
  WarningIcon
} from '@phosphor-icons/react';
import Modal from 'src/components/ui/Modal';
import Button from 'src/components/ui/Button';
import Banner from 'src/components/ui/Banner';
import { twMerge } from 'tailwind-merge';
import { canCreateGroupAction } from 'src/features/groups/permissions';
import type { Group } from 'src/features/groups/types';
import {
  DocumentHubNode,
  isGroupOwnedNode,
  isOrganisationLibraryNode,
  isWorkflowDocumentType,
  workflowMoveWarning
} from '../types';

export type DocumentMoveDestination =
  | { kind: 'personal' }
  | { kind: 'organisation' }
  | { kind: 'group'; groupId: string };

interface DocumentMoveModalProps {
  open: boolean;
  node?: DocumentHubNode | null;
  groups: Group[];
  organisationName?: string;
  tenantName?: string;
  currentGroupId?: string | null;
  currentGroupName?: string | null;
  canMoveToOrganisation: boolean;
  isSubmitting?: boolean;
  /** `link` only lists groups. `add` lists Personal / Organisation / groups for a new file. */
  intent?: 'move' | 'link' | 'add';
  onClose: () => void;
  onSubmit: (destination: DocumentMoveDestination) => Promise<void> | void;
}

function isOwnedInCurrentGroup(node: DocumentHubNode, currentGroupId: string | null | undefined): boolean {
  return !!currentGroupId && node.group_id === currentGroupId && node.group_relation === 'owned';
}

export const isInOrganisationLibrary = isOrganisationLibraryNode;

function isInPersonalFolder(node: DocumentHubNode, currentGroupId: string | null | undefined): boolean {
  if (isInOrganisationLibrary(node)) return false;
  if (isGroupOwnedNode(node) || isOwnedInCurrentGroup(node, currentGroupId)) return false;
  return node.document_type === 'upload' || isWorkflowDocumentType(node.document_type) || !node.document_type;
}

function isGroupOwned(node: DocumentHubNode, currentGroupId: string | null | undefined): boolean {
  return isGroupOwnedNode(node) || isOwnedInCurrentGroup(node, currentGroupId);
}

function sourceGroupName(
  node: DocumentHubNode,
  currentGroupId: string | null | undefined,
  currentGroupName: string | null | undefined,
  groups: Group[]
): string {
  return (
    currentGroupName?.trim() ||
    groups.find((g) => g.id === (node.group_id ?? currentGroupId))?.name ||
    'the previous group'
  );
}

interface AccessNotice {
  message: string;
  /** True when the move narrows who can see the file. */
  losesAccess: boolean;
}

function destinationAccessWarning(
  node: DocumentHubNode,
  selected: string | null,
  {
    tenantName,
    currentGroupId,
    currentGroupName,
    groups
  }: {
    tenantName: string | undefined;
    currentGroupId: string | null | undefined;
    currentGroupName: string | null | undefined;
    groups: Group[];
  }
): AccessNotice | null {
  if (!selected) return null;
  const tenant = tenantName?.trim() || 'your organisation';
  const destKind = selected === 'personal' || selected === 'organisation' ? selected : 'group';
  const destGroupName =
    destKind === 'group' && selected.startsWith('group:')
      ? groups.find((g) => g.id === selected.slice('group:'.length))?.name?.trim()
      : undefined;
  const destGroup = destGroupName || 'the selected group';
  const fromPersonal = isInPersonalFolder(node, currentGroupId);
  const fromOrg = isInOrganisationLibrary(node);
  const fromGroup = isGroupOwned(node, currentGroupId);
  const fromName = sourceGroupName(node, currentGroupId, currentGroupName, groups);

  if (fromPersonal) {
    if (destKind === 'organisation') {
      return { message: `Anyone in ${tenant} will be able to see this file.`, losesAccess: false };
    }
    if (destKind === 'group') {
      return { message: `Members of ${destGroup} will be able to see this file.`, losesAccess: false };
    }
    return null;
  }

  if (fromOrg) {
    if (destKind === 'personal') {
      return { message: `All members of ${tenant} will lose access.`, losesAccess: true };
    }
    if (destKind === 'group') {
      return { message: `Members of ${tenant} who are not in ${destGroup} will lose access.`, losesAccess: true };
    }
    return null;
  }

  if (fromGroup) {
    if (destKind === 'personal') {
      return {
        message:
          fromName === 'the previous group'
            ? 'Members of the previous group will lose access.'
            : `Members of ${fromName} will lose access.`,
        losesAccess: true
      };
    }
    if (destKind === 'group') {
      return {
        message:
          fromName === 'the previous group'
            ? `Members of the previous group who are not in ${destGroup} will lose access.`
            : `Members of ${fromName} who are not in ${destGroup} will lose access.`,
        losesAccess: true
      };
    }
    if (destKind === 'organisation') {
      return { message: `Anyone in ${tenant} will be able to see this file.`, losesAccess: false };
    }
  }

  return null;
}

function DestinationOption({
  option,
  selected,
  onSelect
}: {
  option: { id: string; label: string; subtitle?: string; icon: ReactNode };
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={twMerge(
        'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
        selected ? 'border-secondary-blue bg-blue-50' : 'border-border-default hover:bg-surface-hover'
      )}
    >
      <span className="text-content-subtle">{option.icon}</span>
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-content-default">{option.label}</span>
        {option.subtitle && <span className="text-xs text-content-subtle">{option.subtitle}</span>}
      </span>
    </button>
  );
}

export default function DocumentMoveModal({
  open,
  node,
  groups,
  organisationName = 'Organisation Folder',
  tenantName,
  currentGroupId,
  currentGroupName,
  canMoveToOrganisation,
  isSubmitting,
  intent = 'move',
  onClose,
  onSubmit
}: DocumentMoveModalProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const isLink = intent === 'link';
  const isAdd = intent === 'add';

  useEffect(() => {
    setSelected(null);
  }, [open, node?.id]);

  const groupOptions = useMemo(() => {
    if (!isAdd && !node) return [];
    const ownerGroupId = node?.group_id ?? (node?.group_relation === 'owned' ? currentGroupId : null);
    return groups
      .filter((g) => canCreateGroupAction(g))
      .filter((g) => !(ownerGroupId && g.id === ownerGroupId))
      .filter((g) => !(isLink && currentGroupId && g.id === currentGroupId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, node, currentGroupId, isLink, isAdd]);

  if (!open) return null;
  if (!isAdd && !node) return null;

  const showPersonal = isAdd || (!isLink && node && !isInPersonalFolder(node, currentGroupId));
  const showOrganisation =
    (isAdd && canMoveToOrganisation) ||
    (!isLink && !isAdd && canMoveToOrganisation && !!node && !isInOrganisationLibrary(node));

  const libraryOptions: { id: string; label: string; subtitle?: string; icon: ReactNode }[] = [];
  if (showPersonal) {
    libraryOptions.push({
      id: 'personal',
      label: 'Personal Folder',
      subtitle: 'Only you will be able to see the document',
      icon: <BooksIcon size={20} weight="light" />
    });
  }
  if (showOrganisation) {
    libraryOptions.push({
      id: 'organisation',
      label: organisationName,
      subtitle: 'Anyone in your organisation will be able to see the document',
      icon: <BuildingsIcon size={20} weight="light" />
    });
  }
  const options = [
    ...libraryOptions,
    ...groupOptions.map((group) => ({
      id: `group:${group.id}`,
      label: group.name,
      icon: <UsersThreeIcon size={20} weight="light" />
    }))
  ];

  const handleSubmit = async () => {
    if (!selected) return;
    if ((isAdd || !isLink) && selected === 'personal') {
      await onSubmit({ kind: 'personal' });
      return;
    }
    if ((isAdd || !isLink) && selected === 'organisation') {
      await onSubmit({ kind: 'organisation' });
      return;
    }
    if (selected.startsWith('group:')) {
      await onSubmit({ kind: 'group', groupId: selected.slice('group:'.length) });
    }
  };

  const accessNotice =
    !isAdd && !isLink && node
      ? destinationAccessWarning(node, selected, {
          tenantName,
          currentGroupId,
          currentGroupName,
          groups
        })
      : null;
  const banner = isAdd
    ? selected === 'organisation'
      ? `Anyone in ${tenantName?.trim() || 'your organisation'} will be able to see this file.`
      : selected?.startsWith('group:')
        ? `Members of ${groups.find((g) => g.id === selected.slice('group:'.length))?.name?.trim() || 'the selected group'} will be able to see this file.`
        : null
    : isLink
      ? selected
        ? 'The file stays in the Organisation Folder and also appears in this group.'
        : null
      : (accessNotice?.message ?? null);
  const losesAccess = !!accessNotice?.losesAccess;

  const modalTitle = isAdd ? 'Export to Document Hub' : isLink ? 'Link to group' : 'Move file';
  const confirmLabel = isAdd ? 'Export' : isLink ? 'Link' : 'Move';
  const confirmIcon = isAdd ? PlusIcon : isLink ? LinkSimpleIcon : ArrowBendUpRightIcon;

  return (
    <Modal open={open} onClose={onClose} title={modalTitle} className="w-full max-w-md">
      <div className="flex flex-col gap-3">
        {options.length === 0 ? (
          <p className="text-sm text-content-subtle">
            {isAdd
              ? 'There are no locations you can add this file to.'
              : isLink
                ? 'There are no groups you can link this file into.'
                : 'There are no other locations you can move this file to.'}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-content-subtle">{isLink ? 'Select a group' : 'Select a destination'}</p>
            <div className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto">
              {libraryOptions.length > 0 && (
                <div className="flex flex-col gap-1">
                  {libraryOptions.map((option) => (
                    <DestinationOption
                      key={option.id}
                      option={option}
                      selected={selected === option.id}
                      onSelect={() => setSelected(option.id)}
                    />
                  ))}
                </div>
              )}
              {groupOptions.length > 0 && (
                <div
                  className={twMerge(
                    'flex flex-col gap-1',
                    libraryOptions.length > 0 && 'border-t border-border-default pt-3'
                  )}
                >
                  {!isLink && <p className="px-0.5 text-xs font-medium text-content-subtle">Group Folders</p>}
                  {groupOptions.map((group) => {
                    const id = `group:${group.id}`;
                    return (
                      <DestinationOption
                        key={id}
                        option={{
                          id,
                          label: group.name,
                          icon: <UsersThreeIcon size={20} weight="light" />
                        }}
                        selected={selected === id}
                        onSelect={() => setSelected(id)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {!isAdd && !isLink && node && isWorkflowDocumentType(node.document_type) && (
          <Banner variant="info" icon={InfoIcon}>
            {workflowMoveWarning([node.document_type])}
          </Banner>
        )}
        {banner && (
          <Banner variant={losesAccess ? 'warning' : 'info'} icon={losesAccess ? WarningIcon : InfoIcon}>
            {banner}
          </Banner>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" text="Cancel" onClick={onClose} disabled={isSubmitting} />
          <Button
            variant="primary"
            icon={confirmIcon}
            text={confirmLabel}
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || !selected || options.length === 0}
          />
        </div>
      </div>
    </Modal>
  );
}
